/**
 * Created by jonas on 2021-02-02.
 */
import {GameModel} from "@nolimitcity/slot-game/bin/model/GameModel";
import {
    BetWayWinData,
    HighLowTransformData,
    IBotTrain,
    IMainReelXWays,
    ITopTrain,
    IXSplits,
    IXWays,
    TrueGritData,
    TrueGritGameData,
    TrueGritInitData
} from "./server/TrueGritData";
import {ApiPlugin} from "@nolimitcity/slot-launcher/bin/plugins/ApiPlugin";
import {TrueGrit} from "./TrueGrit";
import {TrueGritGameMode} from "./server/TrueGritGameMode";
import {StateMatrix} from "./utils/StateMatrix";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {APIFeatureBetEventData} from "@nolimitcity/slot-launcher/bin/interfaces/APIBetEventData";

export class TrueGritModel extends GameModel<TrueGritInitData, TrueGritGameData> {
    private _isRestore:boolean = false;
    private _allWinPositions:boolean[][];
    private _avalancheCountThisSpinAfter:number;
    private _avalancheCountThisSpinBefore:number;
    private _winRatio:number;
    private _topTrain:ITopTrain;
    private _totalAvalancheRoundWinnings:number;
    private _betWayWins:BetWayWinData[];
    private _botTrain:IBotTrain;
    private _numberOfScattersOnReels:number;
    private _numberOfScattersBefore:number;
    private _numberOfGritGirlsOnReels:number;
    private _numberOfGritGirlsBefore:number;
    private _freespinsLeft:number;
    private _avalancheCollapseComplete:boolean = false;
    private _superFS:boolean;
    private _reelsAfterCollapse:string[][];
    private _xSplits:IXSplits;
    private _reelSplitLongReel:string[];
    private _xWays:IXWays;
    private _greatBookOfWays:number[][];
    private _waysTotalAvalanches:number;
    private _accumulatedRoundWin:number;
    private _highLowTransform:HighLowTransformData | undefined;
    private _mainReelXWays:IMainReelXWays[];
    private _xWaysMainReelsSymbol:string;
    private _luckyDraw:boolean;
    private _wasFeatureBuy:boolean;
    private _superFSScatterPosition:PIXI.Point;
    private _allScatterPositions:PIXI.Point[] = [];
    private _gritGirlNearWinReels:boolean[] = [];
    private _scatterNearWinReels:boolean[] = [];
    private _highestNearWinScatterReel:number = -1;
    private _isTransformSymbolPartOfWin:boolean;

    private static level01BoxPositions:PIXI.Point[] = [new PIXI.Point(2, 4), new PIXI.Point(3, 3), new PIXI.Point(4, 2), new PIXI.Point(5, 1)];
    private static level02BoxPositions:PIXI.Point[] = [new PIXI.Point(3, 4), new PIXI.Point(4, 3), new PIXI.Point(5, 2)];
    private static level03BoxPositions:PIXI.Point[] = [new PIXI.Point(4, 4), new PIXI.Point(5, 3)];
    private static boxPositions:PIXI.Point[][] = [TrueGritModel.level01BoxPositions, TrueGritModel.level02BoxPositions, TrueGritModel.level03BoxPositions];

    public static defaultNumberOfWaysForScatters:number[] = [240, 800, 3000, 6000, 8000];
    public static canGritGirlChangeState:boolean = false;
    public static previousReelsAfterCollapse:string[][] = [];
    public static lastActiveReelInFirst4:number = -1;

    // #295
    public static previousFSLeft:number = 0;
    public unlockedPositions:StateMatrix<boolean> | undefined;

    public buyFeatureName:string = "";

    constructor(api:ApiPlugin, triggerEvents:boolean = true) {
        super(api, triggerEvents);

        api.events.once("featureName", (data:string) => this.buyFeatureName = data);
        api.events.on(APIEvent.BET, (betData:APIFeatureBetEventData) => {
            for (let index:number = 0; index < TrueGrit.model.initData.featureBuyTimesBetValue.length; index++) {
                if (TrueGrit.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });

        api.events.on(APIEvent.ACTION_SPINS_BET, (betData:APIFeatureBetEventData) => {
            for (let index:number = 0; index < TrueGrit.model.initData.featureBuyTimesBetValue.length; index++) {
                if (TrueGrit.model.initData.featureBuyTimesBetValue[index].name === betData.featureName) {
                    this.buyFeatureName = betData.featureName;
                    break;
                }
            }
        });
    }

    protected onInit(data:TrueGritInitData):void {
        this.parseGameMode(data);
        //console.log(data);

        super.onInit(data);
        this._reelsAfterCollapse = this.parseInitReelsAfterCollapse(data);
        this._superFS = data.superFS;
        this._isRestore = data.isRestoreState;
        this._topTrain = data.topTrain;
        this._botTrain = data.botTrain;
        this._avalancheCountThisSpinAfter = data.avalancheCountThisSpinAfter;
        this._avalancheCountThisSpinBefore = data.avalancheCountThisSpinBefore;
        this._freespinsLeft = data.freespinsLeft;
        this._numberOfScattersOnReels = this.parseNumberOfScattersOnReels(data.reels);
        this._numberOfScattersBefore = this._numberOfScattersOnReels;
        this._numberOfGritGirlsOnReels = this.parseNumberOfGritGirlsOnReels(data.reels);
        this._numberOfGritGirlsBefore = this._numberOfGritGirlsOnReels;
        this._waysTotalAvalanches = data.waysTotalAvalanches;
        this._accumulatedRoundWin = data.accumulatedRoundWin;
        this._topTrain = this.parseTopTrainData();
        if(this._isRestore && (TrueGrit.model.data.nextMode == TrueGritGameMode.FREESPIN_SUPER_AVALANCHE || TrueGrit.model.data.nextMode == TrueGritGameMode.FREESPIN_AVALANCHE
            || TrueGrit.model.data.nextMode == TrueGritGameMode.NORMAL_AVALANCHE)) {
            this._avalancheCollapseComplete = true;
        }

        TrueGritModel.previousReelsAfterCollapse = this._reelsAfterCollapse;
    }

    public onGame(data:TrueGritGameData):void {
        this.parseGameMode(data);
        //console.log(data);

        //Fix for server bug
        if (data.mode == TrueGritGameMode.FREESPIN_SUPER && data.brokeBank == true){
            data.botTrain.symbols = this.parseBotTrainSymbols(data);
        }

        super.onGame(data);
        this._gritGirlNearWinReels = this.parseGritGirlNearWinReels();
        this._scatterNearWinReels = this.parseScatterNearWinReels();
        this._highestNearWinScatterReel = this.parseHighestScatterNearWinReel();
        this._superFSScatterPosition = this.parseSuperFSScatterPosition();
        this._allScatterPositions = this.parseAllScatterPositions();
        this._xWaysMainReelsSymbol = data.xWaysMainReelsSymbol;
        this._wasFeatureBuy = data.wasFeatureBuy;
        this._luckyDraw = data.luckyDraw;
        this._freespinsLeft = data.freespinsLeft;
        this._greatBookOfWays = data.greatBookOfWays;
        this._superFS = data.superFS;
        this._isRestore = false;
        this._betWayWins = data.betWayWins;
        this.parseAllWinSymbols(data.betWayWins);
        this._topTrain = data.topTrain;
        this._avalancheCountThisSpinAfter = data.avalancheCountThisSpinAfter;
        this._avalancheCountThisSpinBefore = data.avalancheCountThisSpinBefore;
        this._totalAvalancheRoundWinnings = data.totalAvalancheRoundWinnings;
        this._numberOfScattersOnReels = this.parseNumberOfScattersOnReels(data.reels);
        this._numberOfScattersBefore = this._numberOfScattersOnReels;
        this._numberOfGritGirlsOnReels = this.parseNumberOfGritGirlsOnReels(data.reels);
        this._numberOfGritGirlsBefore = this._numberOfGritGirlsOnReels;
        this._xWays = data.xWays;
        this._botTrain = data.botTrain;
        this._botTrain = this.parseXWaySymbols(this._botTrain);
        this._numberOfScattersBefore = this.parseNumberOfScattersBefore();
        this._numberOfScattersOnReels = this.parseNumberOfScattersOnReels(data.reels);
        this._xSplits = data.xSplits;
        this._accumulatedRoundWin = data.accumulatedRoundWin;
        this._highLowTransform = data.highLowTransform;
        this._mainReelXWays = this.parseMainReelsXWaysSymbols(data);
        this._botTrain = this.parseBotTrainData();
        this._topTrain = this.parseTopTrainData();
        this.unlockedPositions = this.parseLockedPositions(data.botTrain.isRevealed);
        this._isTransformSymbolPartOfWin = this.parseTranformPartOfWin(data);
        if(this._xSplits) {
            // #484
            this.doXSplitsCorrection(data);

            this._reelSplitLongReel = this.calculateReelSplitLongReel();
        }
        if(TrueGrit.model.data.mode == TrueGritGameMode.NORMAL || TrueGrit.model.data.mode == TrueGritGameMode.NORMAL_AVALANCHE) {
            this._winRatio = data.accumulatedRoundWin / data.playedBetValue;
        } else {
            this._winRatio = data.totalAvalancheRoundWinnings / data.playedBetValue;
        }
    }

    private parseBotTrainSymbols(data:TrueGritGameData):string[]{
        const botTrainSymbols:string[] = [];
        botTrainSymbols.push(data.reels[2][4]);
        botTrainSymbols.push(data.reels[3][4]);
        botTrainSymbols.push(data.reels[4][4]);
        botTrainSymbols.push(data.reels[5][4]);

        return botTrainSymbols;
    }

    private parseTranformPartOfWin(data:TrueGritGameData):boolean {
        if(data.highLowTransform) {
            for(let i:number = 0; i < TrueGrit.model.allWinPositions.length; i++) {
                for(let j:number = 0; j < TrueGrit.model.allWinPositions[i].length; j++) {
                    if(TrueGrit.model.allWinPositions[i][j] == true && data.highLowTransform.toTransform[i][j] == true) {
                        return true;
                    }
                }
            }
        }
        return false;
    }

    // #484
    private doXSplitsCorrection(data:TrueGritGameData):void {
        if(data.wasFeatureBuy && data.mode === TrueGritGameMode.NORMAL && (data.nextMode === TrueGritGameMode.FREESPIN || data.nextMode === TrueGritGameMode.FREESPIN_SUPER)) {
            const bookOfXWays:number[][] = data.greatBookOfWays;

            // check for 2 i.e single split.
            for(let reel:number = 2; reel < bookOfXWays.length - 1; reel++) {
                // could have checked only 1 position, but to be safe checking all possible positions.
                if(bookOfXWays[reel][1] === 2 && bookOfXWays[reel][2] === 2 && bookOfXWays[reel][3] === 2) {
                    this._xSplits.reelsSplit[reel - 2] = true;

                    return;
                }
            }

            // if 2 not found check for 4 i.e double split.
            for(let reel:number = 2; reel < bookOfXWays.length - 1; reel++) {
                // could have checked only 1 position, but to be safe checking all possible positions.
                if(bookOfXWays[reel][1] === 4 && bookOfXWays[reel][2] === 4 && bookOfXWays[reel][3] === 4) {
                    this._xSplits.reelsSplit[reel - 2] = true;
                    this._xSplits.secondSplit[reel - 2] = true;

                    return;
                }
            }
        }
    }

    private parseLockedPositions(isRevealed:boolean[]):StateMatrix<boolean> {
        const result = new StateMatrix<boolean>();
        result.initWithValue(6, 5, true);

        if(!isRevealed[0]) {
            result.matrix[3][3] = false;
        }

        if(!isRevealed[1]) {
            result.matrix[4][2] = false;
            result.matrix[4][3] = false;
        }

        if(!isRevealed[2]) {
            result.matrix[5][1] = false;
            result.matrix[5][2] = false;
            result.matrix[5][3] = false;
        }

        return result;
    };

    //Fixing server bug. Don't do transform on Wild
    private parseTopTrainData():ITopTrain {
        for(let i:number = 0; i < this._topTrain.transformSpellActivated.length; i++) {
            if(this._topTrain.transformSpellActivated[i] == true && this._topTrain.beforeSpellSymbols[i - 1] == "W") {
                this._topTrain.transformSpellActivated[i] = false;
                for(let j:number = 0; j < TrueGrit.model.data.reels[i].length; j++) {
                    if(TrueGrit.model.data.reels[i][j] == "W") {
                        TrueGrit.model.topTrain.transformSpellTransform[i][j] = false;
                    }
                }
            }
        }
        return this._topTrain;
    }

    //Need to parse botTrain to fix server bug where M symbol are sent incorrectly.
    //If so, it's replaced by correct symbol from reels data
    private parseBotTrainData():IBotTrain {
        if(this._botTrain.symbols.includes("M") == false) {
            return this._botTrain;
        } else {
            //We have one or several M symbols
            if(!this._botTrain.highsLowTrain.M) {
                //No M data. Replace all M symbols.
                for(let i:number = 0; i < this._botTrain.symbols.length; i++) {
                    if(this._botTrain.symbols[i] == "M" && this._botTrain.isRevealed[i] == true) {
                        //Replace symbol
                        this._botTrain.symbols[i] = TrueGrit.model.data.reels[i + 2][4];
                    }
                }
            } else {
                for(let i:number = 0; i < this._botTrain.highsLowTrain.M.length; i++) {
                    if(this._botTrain.highsLowTrain.M[i] == false && this._botTrain.symbols[i] == "M") {
                        //Replace symbol
                        this._botTrain.symbols[i] = TrueGrit.model.data.reels[i + 2][4];
                    }
                }
            }
        }

        return this._botTrain;
    }

    private static createNewSymbols():string[][] {
        let newSymbols:string[][] = [[], [], [], [], [], []];

        if(TrueGrit.model.data.mode.indexOf("AVALANCHE") > -1) {
            const previousReelsAfterCollapse:string[][] = TrueGritModel.previousReelsAfterCollapse,
                reels:string[][] = TrueGrit.model.data.reels;

            for(let i:number = 0; i < reels.length; i++) {
                for(let j:number = 0; j < reels[i].length; j++) {
                    if(previousReelsAfterCollapse[i][j] === "X") {
                        newSymbols[i][j] = reels[i][j];
                    } else {
                        break;
                    }
                }
            }
        }

        return newSymbols;
    }

    private parseGritGirlNearWinReels():boolean[] {
        TrueGritModel.canGritGirlChangeState = false;
        const isWildMulti1:boolean = (TrueGrit.model.data.botTrain.wildMult === 1),
            isAvalanche:boolean = (TrueGrit.model.data.mode.indexOf("AVALANCHE") > -1),
            newSymbols:string[][] = TrueGritModel.createNewSymbols(),
            reels:string[][] = TrueGrit.model.data.reels;

        // #310
        if(isAvalanche) {
            for(let i:number = 3; i >= 0; i--) {
                if(newSymbols[i].length > 1) {
                    TrueGritModel.lastActiveReelInFirst4 = i;
                    break;
                }
            }
        } else {
            TrueGritModel.lastActiveReelInFirst4 = 3;
        }

        let nearWinReels:boolean[] = [false, false, false, false, false, false],
            gritGirlCounter:number = 0;

        let gritGirlPositions:number[] = [-1, -1, -1, -1],
            normalSecondGritGirlReel:number = -1;
        for(let reel:number = 0; reel < reels.length - 2; reel++) {
            for(let symId:number = 1; symId < reels[reel].length - 1; symId++) {
                if(reels[reel][symId] === "M0") {
                    gritGirlPositions[reel] = symId;
                    gritGirlCounter++;

                    if(normalSecondGritGirlReel === -1 && gritGirlCounter === 2) {
                        normalSecondGritGirlReel = (reel + 1);
                    }
                    break;
                }
            }
        }

        if(!isWildMulti1) {
            if(gritGirlCounter > 1) {
                TrueGritModel.canGritGirlChangeState = true;

                if(isAvalanche) {
                    let newGirls:number = 0,
                        newGirlPositions:boolean[] = [false, false, false, false],
                        firstNewGirlReel:number = -1,
                        secondNewGirlReel:number = -1,
                        initI:number = -1;
                    for(let reel:number = 0; reel < reels.length - 2; reel++) {
                        if(newSymbols[reel].includes("M0")) {
                            newGirlPositions[reel] = true;
                            newGirls++;

                            if(newGirls === 1) {
                                firstNewGirlReel = (reel + 1);
                            } else if(newGirls === 2) {
                                secondNewGirlReel = (reel + 1);
                            }
                        }
                    }

                    if((gritGirlCounter - newGirls) > 1) {
                        initI = 0;
                    } else if((gritGirlCounter - newGirls) === 1) {
                        initI = firstNewGirlReel;
                    } else if(gritGirlCounter === newGirls) {
                        initI = secondNewGirlReel;
                    }

                    for(let i:number = initI; i < reels.length - 2; i++) {
                        if(newSymbols[i].length > 1 && !(gritGirlPositions[i] > 0 && !newGirlPositions[i])) {
                            nearWinReels[i] = true;
                        }
                    }
                } else {
                    for(let i:number = normalSecondGritGirlReel; i < reels.length - 2; i++) {
                        nearWinReels[i] = true;
                    }
                }
            }

            return nearWinReels;
        } else {
            if(gritGirlCounter < 3) {
                if(gritGirlCounter === 2) {
                    // checking, if 1 girl is found on 4th reel.
                    TrueGritModel.canGritGirlChangeState = (gritGirlPositions[3] === -1);
                }
            } else {
                TrueGritModel.canGritGirlChangeState = true;

                if((gritGirlCounter === 4) || (gritGirlCounter === 3 && gritGirlPositions[2] > 0)) {
                    nearWinReels[3] = true;
                }
            }

            return nearWinReels;
        }
    }

    private parseScatterNearWinReels():boolean[] {
        let nearWinReels:boolean[] = [false, false, false, false, false, false];

        // #323 => if lucky draw, always anticipate for 3rd, 4th scatter which is possible only on 3rd, 4th reel respectively, and no 2 scatters can land on same reel #88.
        if(TrueGrit.model.gameData.luckyDraw && TrueGrit.model.gameData.mode === TrueGritGameMode.NORMAL) {
            nearWinReels[2] = true;
            nearWinReels[3] = true;

            return nearWinReels;
        } else if(TrueGrit.model.gameData.wasFeatureBuy && TrueGrit.model.gameData.mode === TrueGritGameMode.NORMAL) {
            return nearWinReels;
        }

        // if neither NORMAL nor NORMAL_AVALANCHE, don't do anything
        if(TrueGrit.model.data.mode.indexOf("NORMAL") === -1) {
            return nearWinReels;
        }

        let scattersCount:number = 0,
            scatterPositions:number[] = [-1, -1, -1, -1],
            isNormalAvalanche:boolean = (TrueGrit.model.data.mode === TrueGritGameMode.NORMAL_AVALANCHE),
            newSymbols:string[][] = TrueGritModel.createNewSymbols(),
            reels:string[][] = TrueGrit.model.data.reels,
            normalSecondScatterReel:number = -1;

        for(let reel:number = 0; reel < reels.length - 2; reel++) {
            for(let symId:number = 1; symId < reels[reel].length - 1; symId++) {
                if(reels[reel][symId] === "S") {
                    scatterPositions[reel] = symId;
                    scattersCount++;

                    if(normalSecondScatterReel === -1 && scattersCount === 2) {
                        normalSecondScatterReel = (reel + 1);
                    }
                    break;
                }
            }
        }

        if(scattersCount > 1) {
            if(isNormalAvalanche) {
                let newScatters:number = 0,
                    newScatterPositions:boolean[] = [false, false, false, false],
                    firstNewScatterReel:number = -1,
                    secondNewScatterReel:number = -1,
                    thirdNewScatterReel:number = -1,
                    initI:number = -1;
                for(let reel:number = 0; reel < reels.length - 2; reel++) {
                    if(newSymbols[reel].includes("S")) {
                        newScatterPositions[reel] = true;
                        newScatters++;

                        if(newScatters === 1) {
                            firstNewScatterReel = (reel + 1);
                        } else if(newScatters === 2) {
                            secondNewScatterReel = (reel + 1);
                        } else if(newScatters === 3) {
                            thirdNewScatterReel = (reel + 1);
                        }
                    }
                }

                // if already 4 scatters, always send all false.
                if(scattersCount > 3 && newScatters === 0) {
                    return nearWinReels;
                }

                if((scattersCount - newScatters) > 1) {
                    initI = 0;
                } else if((scattersCount - newScatters) === 1) {
                    initI = firstNewScatterReel;
                } else if(scattersCount === newScatters) {
                    initI = secondNewScatterReel;
                }

                for(let i:number = initI; i < reels.length - 2; i++) {
                    if(newSymbols[i].length > 1 && !(scatterPositions[i] > 0 && !newScatterPositions[i])) {
                        nearWinReels[i] = true;
                    }
                }

                // if count is 4, we shall stop when we see the 4th one.
                if(scattersCount === 4) {
                    if(newScatters === 1) {
                        initI = firstNewScatterReel;
                    } else if(newScatters === 2) {
                        initI = secondNewScatterReel;
                    } else if(newScatters === 3) {
                        initI = thirdNewScatterReel;
                    }

                    for(let i:number = initI; i < reels.length - 2; i++) {
                        nearWinReels[i] = false;
                    }
                }
            } else {
                for(let i:number = normalSecondScatterReel; i < reels.length - 2; i++) {
                    nearWinReels[i] = true;
                }
            }
        }

        return nearWinReels;
    }

    private parseHighestScatterNearWinReel():number {
        if(this._scatterNearWinReels.includes(true)) {
            let highestReelNumber:number = -1;
            for(let i:number = 0; i < this._scatterNearWinReels.length; i++) {
                if(this._scatterNearWinReels[i] == true) {
                    highestReelNumber = i;
                }
            }
            return highestReelNumber;
        } else {
            return -1;
        }
    }

    private parseSuperFSScatterPosition():PIXI.Point {
        let position:PIXI.Point = new PIXI.Point(-1, -1);
        if(TrueGrit.model.data.nextMode == TrueGritGameMode.FREESPIN_SUPER || TrueGrit.model.data.mode == TrueGritGameMode.FREESPIN_SUPER || TrueGrit.model.data.mode == TrueGritGameMode.FREESPIN_SUPER_AVALANCHE) {
            for(let i:number = TrueGrit.model.data.reels.length - 3; i > -1; i--) {
                for(let j:number = 0; j < TrueGrit.model.data.reels[i].length; j++) {
                    if(TrueGrit.model.data.reels[i][j] == "S") {
                        position.x = i;
                        position.y = j;
                        return position;
                    }
                }
            }
        }
        return position;
    }


    private parseAllScatterPositions() {
        let result = [];
        for(let i:number = 0; i < TrueGrit.model.data.reels.length; i++) {
            for(let j:number = 0; j < TrueGrit.model.data.reels[i].length; j++) {
                let symbol = TrueGrit.model.data.reels[i][j];
                if(symbol == "S") {
                    result.push(new PIXI.Point(i, j));
                }

            }
        }
        return result;
    }

    private parseMainReelsXWaysSymbols(data:TrueGritGameData):IMainReelXWays[] {
        const mainReelXWays:IMainReelXWays[] = [];
        let splitLevel:number = -1;
        for(let i:number = 0; i < data.reels.length; i++) {
            for(let j:number = 1; j < data.reels[i].length - 1; j++) {
                if(data.reels[i][j] == "M") {
                    if(this.checkIfValidPosition(new PIXI.Point(i, j)) == true) {
                        if(this._xWays && this._xWays.isInfectious == true) {
                            splitLevel = 2;
                        } else {
                            splitLevel = data.greatBookOfWays[i][j];
                        }
                        mainReelXWays.push({
                            position : new PIXI.Point(i, j),
                            splitLevel : splitLevel,
                            symbolName : this._xWaysMainReelsSymbol
                        });
                    }
                }
            }
        }
        return mainReelXWays;
    }

    private checkIfValidPosition(position:PIXI.Point):boolean {
        if(position.x > 1 && position.y == 4) {
            //XWays on Bot reel
            return false;
        }
        if(this._botTrain.isRevealed[0] == false) {
            for(let i:number = 0; i < TrueGritModel.boxPositions.length; i++) {
                for(let j:number = 0; j < TrueGritModel.boxPositions[i].length; j++) {
                    if(TrueGritModel.boxPositions[i][j].x == position.x && TrueGritModel.boxPositions[i][j].y == position.y) {
                        return false;
                    }
                }
            }
        }
        if(this._botTrain.isRevealed[0] == true && this._botTrain.isRevealed[1] == false) {
            for(let i:number = 1; i < TrueGritModel.boxPositions.length; i++) {
                for(let j:number = 0; j < TrueGritModel.boxPositions[i].length; j++) {
                    if(TrueGritModel.boxPositions[i][j].x == position.x && TrueGritModel.boxPositions[i][j].y == position.y) {
                        return false;
                    }
                }
            }
        }
        if(this._botTrain.isRevealed[1] == true && this._botTrain.isRevealed[2] == false) {
            for(let i:number = 2; i < TrueGritModel.boxPositions.length; i++) {
                for(let j:number = 0; j < TrueGritModel.boxPositions[i].length; j++) {
                    if(TrueGritModel.boxPositions[i][j].x == position.x && TrueGritModel.boxPositions[i][j].y == position.y) {
                        return false;
                    }
                }
            }
        }
        return true;
    }

    //If infectious ALL M should be changed to M_infectious
    private parseXWaySymbols(botTrain:IBotTrain):IBotTrain {
        if(this._xWays && this._xWays.isInfectious) {
            for(let i:number = 0; i < botTrain.symbols.length; i++) {
                if(botTrain.symbols[i] === "M") {
                    botTrain.symbols[i] = "M_infectious";
                }
            }
        }
        return botTrain;
    }

    private calculateReelSplitLongReel():string[] {
        const botReelWithSplits:string[] = ["", "", "", "", "", ""];
        let splitIndex:number = 0;
        const returnReel:string[] = [];

        for(let botReelIndex:number = 0; botReelIndex < 3; botReelIndex++) {
            if(this._xSplits.reelsSplit[botReelIndex]) {
                botReelWithSplits[botReelIndex + splitIndex] = "Z";
                splitIndex++;
            }
            if(this._xSplits.secondSplit[botReelIndex]) {
                botReelWithSplits[botReelIndex + splitIndex] = "Z";
                splitIndex++;
            }
            botReelWithSplits[botReelIndex + splitIndex] = this.botTrain.symbols[botReelIndex];
        }

        for(let i:number = 0; i < botReelWithSplits.length; i++) {
            if(botReelWithSplits[i] !== "") {
                returnReel.push(botReelWithSplits[i]);
            }
        }

        return returnReel;
    }

    private parseInitReelsAfterCollapse(data:TrueGritInitData):string[][] {

        return data.reelsAfterCollapse;
    }

    private parseGameMode(data:TrueGritData):void {
        if(data.superFS == true) {
            if(data.mode == TrueGritGameMode.FREESPIN_AVALANCHE) {
                data.mode = TrueGritGameMode.FREESPIN_SUPER_AVALANCHE;
            }
            if(data.nextMode == TrueGritGameMode.FREESPIN_AVALANCHE) {
                data.nextMode = TrueGritGameMode.FREESPIN_SUPER_AVALANCHE;
            }
        }
    }

    private parseNumberOfScattersBefore():number {
        let scatters:number;
        if(TrueGrit.model.data.mode == TrueGritGameMode.NORMAL || TrueGrit.model.data.mode == TrueGritGameMode.FREESPIN) {
            scatters = 0;
        } else {
            scatters = this._numberOfScattersOnReels;
        }
        return scatters;
    }

    private parseNumberOfScattersOnReels(reels:string[][]):number {
        let scatters:number = 0;
        if(TrueGrit.model.data.nextMode !== TrueGritGameMode.NORMAL || TrueGrit.model.data.nextMode !== TrueGritGameMode.FREESPIN) {
            for(let i:number = 0; i < reels.length; i++) {
                for(let j:number = 0; j < reels[i].length - 1; j++) {
                    if(reels[i][j] == "S") {
                        if(TrueGrit.model.botTrain.isRevealed[0] == false && (i == 3) && j == 3) {
                            //Scatter landed in locked position
                        } else {
                            scatters++;
                        }
                    }
                }
            }
        }
        return scatters;
    }

    private parseNumberOfGritGirlsOnReels(reels:string[][]):number {
        let gritGirls:number = 0;
        for(let i:number = 0; i < reels.length; i++) {
            for(let j:number = 0; j < reels[i].length - 1; j++) {
                if(reels[i][j] == "M0") {
                    gritGirls++;
                }
            }
        }
        return gritGirls;
    }

    private parseAllWinSymbols(data:BetWayWinData[]):void {
        this._allWinPositions = [];
        //Reset
        let reel:boolean[];
        for(let i:number = 0; i < 6; i++) {
            reel = [];
            for(let j:number = 0; j < 5; j++) {
                reel.push(false);
            }
            this._allWinPositions.push(reel)
        }

        for(let i:number = 0; i < data.length; i++) {
            for(let j:number = 0; j < data[i].reelWinPositions.length; j++) {
                for(let k:number = 0; k < data[i].reelWinPositions[j].length; k++) {
                    if(data[i].reelWinPositions[j][k] == true) {
                        this._allWinPositions[j][k] = true;
                    }
                }
            }
        }
    }

    get botTrain():IBotTrain {
        return this._botTrain;
    }

    get isRestore():boolean {
        return this._isRestore;
    }

    get topTrain():ITopTrain {
        return this._topTrain;
    }

    get avalancheCountThisSpinAfter():number {
        return this._avalancheCountThisSpinAfter;
    }

    get avalancheCountThisSpinBefore():number {
        return this._avalancheCountThisSpinBefore;
    }

    get winRatio():number {
        return this._winRatio;
    }

    get totalAvalancheRoundWinnings():number {
        return this._totalAvalancheRoundWinnings;
    }

    get betWayWins():BetWayWinData[] {
        return this._betWayWins;
    }

    get numberOfScattersOnReels():number {
        return this._numberOfScattersOnReels;
    }

    get numberOfScattersBefore():number {
        return this._numberOfScattersBefore;
    }

    get numberOfGritGirlsOnReels():number {
        return this._numberOfGritGirlsOnReels;
    }

    get numberOfGritGirlsBefore():number {
        return this._numberOfGritGirlsBefore;
    }

    get freespinsLeft():number {
        return this._freespinsLeft;
    }

    set freespinsLeft(value:number) {
        this._freespinsLeft = value;
    }

    get allWinPositions():boolean[][] {
        return this._allWinPositions;
    }

    get avalancheCollapseComplete():boolean {
        return this._avalancheCollapseComplete;
    }

    set avalancheCollapseComplete(value:boolean) {
        this._avalancheCollapseComplete = value;
    }

    get superFS():boolean {
        return this._superFS;
    }

    get reelsAfterCollapse():string[][] {
        return this._reelsAfterCollapse;
    }

    get reelSplitLongReel():string[] {
        return this._reelSplitLongReel;
    }

    get greatBookOfWays():number[][] {
        return this._greatBookOfWays;
    }

    get highLowTransform():HighLowTransformData | undefined {
        return this._highLowTransform;
    }

    get waysTotalAvalanches():number {
        return this._waysTotalAvalanches;
    }

    get accumulatedRoundWin():number {
        return this._accumulatedRoundWin;
    }

    get mainReelXWays():IMainReelXWays[] {
        return this._mainReelXWays;
    }

    get luckyDraw():boolean {
        return this._luckyDraw;
    }

    get wasFeatureBuy():boolean {
        return this._wasFeatureBuy;
    }

    get xWaysMainReelsSymbol():string {
        return this._xWaysMainReelsSymbol;
    }

    get superFSScatterPosition():PIXI.Point {
        return this._superFSScatterPosition;
    }

    get allScatterPositions():PIXI.Point[] {
        return this._allScatterPositions;
    }

    get gritGirlNearWinReels():boolean[] {
        return this._gritGirlNearWinReels;
    }

    get scatterNearWinReels():boolean[] {
        return this._scatterNearWinReels;
    }

    get highestNearWinScatterReel():number {
        return this._highestNearWinScatterReel;
    }

    get isTransformSymbolPartOfWin():boolean {
        return this._isTransformSymbolPartOfWin;
    }
}